import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config/apiConfig';

// ─── Wiki Badge config mapped to trust_level ───────────────────────────────────
const TRUST_BADGES = {
  0: null,
  1: { label: 'Wiki Contributor', icon: '📝', color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  2: { label: 'Trusted Editor',   icon: '✅', color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
  3: { label: 'Senior Editor',    icon: '⭐', color: '#ca8a04', bg: '#fefce8', border: '#fef08a' }
};

const TRUST_THRESHOLDS = [
  { level: 1, required: 3,  label: 'Wiki Contributor' },
  { level: 2, required: 10, label: 'Trusted Editor' },
  { level: 3, required: 25, label: 'Senior Editor' }
];

// ─── Discussion Badge config mapped to discussion_level ─────────────────────────
const DISCUSSION_BADGES = {
  0: null,
  1: { label: 'Active Participant', icon: '💬', color: '#0369a1', bg: '#f0f9ff', border: '#bae6fd' },
  2: { label: 'Community Voice',    icon: '🗣️', color: '#7e22ce', bg: '#faf5ff', border: '#e9d5ff' },
  3: { label: 'Forum Leader',       icon: '🏆', color: '#b45309', bg: '#fffbeb', border: '#fde68a' }
};

const DISCUSSION_THRESHOLDS = [
  { level: 1, required: 10,  label: 'Active Participant' },
  { level: 2, required: 50,  label: 'Community Voice' },
  { level: 3, required: 150, label: 'Forum Leader' }
];

// ─── Shared wiki badge pill renderer (exported for reuse) ─────────────────────
export const WikiBadge = ({ trustLevel, style }) => {
  const badge = TRUST_BADGES[trustLevel];
  if (!badge) return null;
  return (
    <span style={{
      fontSize: '11px',
      fontWeight: '600',
      color: badge.color,
      background: badge.bg,
      border: `1px solid ${badge.border}`,
      borderRadius: '10px',
      padding: '1px 8px',
      whiteSpace: 'nowrap',
      ...style
    }}>
      {badge.icon} {badge.label}
    </span>
  );
};

// ─── Shared discussion badge pill renderer (exported for reuse) ───────────────
export const DiscussionBadge = ({ discussionLevel, style }) => {
  const badge = DISCUSSION_BADGES[discussionLevel];
  if (!badge) return null;
  return (
    <span style={{
      fontSize: '11px',
      fontWeight: '600',
      color: badge.color,
      background: badge.bg,
      border: `1px solid ${badge.border}`,
      borderRadius: '10px',
      padding: '1px 8px',
      whiteSpace: 'nowrap',
      ...style
    }}>
      {badge.icon} {badge.label}
    </span>
  );
};

// ─── Industry VIP badge (exported for reuse) ──────────────────────────────────
export const IndustryVIPBadge = ({ style }) => (
  <span style={{
    fontSize: '11px',
    fontWeight: '700',
    color: '#7c2d12',
    background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
    border: '1px solid #f59e0b',
    borderRadius: '10px',
    padding: '1px 8px',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    ...style
  }}>
    ⭐ Industry VIP
  </span>
);

// ─── Progress bar toward next trust level ─────────────────────────────────────
export const TrustProgressBar = ({ trustLevel, cleanApprovedEdits }) => {
  const nextTier = TRUST_THRESHOLDS.find(t => t.level > trustLevel);
  if (!nextTier) {
    return (
      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
        ⭐ Maximum trust level reached!
      </div>
    );
  }

  const progress = Math.min(cleanApprovedEdits / nextTier.required, 1);
  return (
    <div style={{ marginTop: '0.25rem' }}>
      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.35rem' }}>
        {cleanApprovedEdits} of {nextTier.required} clean approvals toward <strong>{nextTier.label}</strong>
      </div>
      <div style={{
        height: '8px',
        background: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${progress * 100}%`,
          background: 'linear-gradient(90deg, #6c5ce7, #a29bfe)',
          borderRadius: '4px',
          transition: 'width 0.5s ease'
        }} />
      </div>
    </div>
  );
};

// ─── Progress bar toward next discussion level ────────────────────────────────
export const DiscussionProgressBar = ({ discussionLevel, totalMessages }) => {
  const nextTier = DISCUSSION_THRESHOLDS.find(t => t.level > discussionLevel);
  if (!nextTier) {
    return (
      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
        🏆 Maximum discussion level reached!
      </div>
    );
  }

  const progress = Math.min(totalMessages / nextTier.required, 1);
  return (
    <div style={{ marginTop: '0.25rem' }}>
      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.35rem' }}>
        {totalMessages} of {nextTier.required} posts toward <strong>{nextTier.label}</strong>
      </div>
      <div style={{
        height: '8px',
        background: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${progress * 100}%`,
          background: 'linear-gradient(90deg, #0ea5e9, #7dd3fc)',
          borderRadius: '4px',
          transition: 'width 0.5s ease'
        }} />
      </div>
    </div>
  );
};

// ─── User Photo Component ─────────────────────────────────────────────────────
const LeaderboardPhoto = ({ userId, displayName, hasPhoto }) => {
  const [imgError, setImgError] = useState(false);
  const initial = (displayName || 'U').charAt(0).toUpperCase();

  if (!hasPhoto || imgError) {
    return (
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: '#e2e8f0', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '0.9rem', fontWeight: '600',
        color: '#64748b', flexShrink: 0
      }}>
        {initial}
      </div>
    );
  }

  return (
    <img
      src={`${API_URL}/users/${userId}/photo`}
      alt={displayName}
      onError={() => setImgError(true)}
      style={{
        width: 36, height: 36, borderRadius: '50%',
        objectFit: 'cover', flexShrink: 0
      }}
    />
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ContributorsScreen = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('wiki');
  const [wikiLeaders, setWikiLeaders] = useState([]);
  const [discLeaders, setDiscLeaders] = useState([]);
  const [myWikiStats, setMyWikiStats] = useState(null);
  const [myDiscStats, setMyDiscStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Fetch both leaderboards in parallel
        const [wikiRes, discRes] = await Promise.all([
          fetch(`${API_URL}/wiki/leaderboard?limit=50`, { headers }),
          fetch(`${API_URL}/discussions/leaderboard?limit=50`, { headers })
        ]);

        if (wikiRes.ok) setWikiLeaders(await wikiRes.json());
        if (discRes.ok) setDiscLeaders(await discRes.json());

        // Fetch own stats if logged in
        if (user?.userId) {
          const [wikiStatsRes, discStatsRes] = await Promise.all([
            fetch(`${API_URL}/wiki/stats/${user.userId}`, { headers }),
            fetch(`${API_URL}/discussions/stats/${user.userId}`, { headers })
          ]);
          if (wikiStatsRes.ok) setMyWikiStats(await wikiStatsRes.json());
          if (discStatsRes.ok) setMyDiscStats(await discStatsRes.json());
        }
      } catch (err) {
        console.error('Failed to load contributors data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user?.userId]);

  const rankStyle = (rank) => {
    if (rank === 1) return { color: '#ca8a04', fontWeight: '700', fontSize: '1.1rem' };
    if (rank === 2) return { color: '#6b7280', fontWeight: '700', fontSize: '1.05rem' };
    if (rank === 3) return { color: '#b45309', fontWeight: '700', fontSize: '1.05rem' };
    return { color: '#6b7280', fontWeight: '600' };
  };

  const rankLabel = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const tabStyle = (tab) => ({
    flex: 1,
    padding: '0.6rem 1rem',
    border: 'none',
    borderBottom: activeTab === tab ? '3px solid #6c5ce7' : '3px solid transparent',
    background: activeTab === tab ? 'white' : '#f8f9fa',
    color: activeTab === tab ? '#6c5ce7' : '#6b7280',
    fontWeight: activeTab === tab ? '700' : '500',
    fontSize: '0.95rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  });

  const leaders = activeTab === 'wiki' ? wikiLeaders : discLeaders;

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2d1b69 0%, #11998e 100%)',
          borderRadius: '12px',
          padding: '2rem 2.5rem',
          marginBottom: '1.5rem',
          textAlign: 'center',
          color: 'white'
        }}>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.6rem', fontWeight: '700' }}>
            🏆 Community Contributors
          </h1>
          <p style={{ margin: 0, opacity: 0.85, fontSize: '0.95rem' }}>
            Recognising members who make AmpNebula better for everyone
          </p>
        </div>

        {/* My Stats Card (logged-in users) */}
        {user && (
          (myWikiStats && myWikiStats.total_edits > 0) ||
          (myDiscStats && myDiscStats.total_messages > 0)
        ) && (
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '1.25rem 1.5rem',
            border: '1px solid #e9ecef',
            marginBottom: '1.5rem'
          }}>
            <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333', marginBottom: '0.75rem' }}>
              Your Contributions
            </div>

            {/* Wiki stats row */}
            {myWikiStats && myWikiStats.total_edits > 0 && (
              <div style={{ marginBottom: myDiscStats && myDiscStats.total_messages > 0 ? '1rem' : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#555' }}>Wiki</span>
                  <WikiBadge trustLevel={myWikiStats.trust_level} />
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6c5ce7' }}>{myWikiStats.approved_edits}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Approved</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#333' }}>{myWikiStats.total_edits}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Submitted</div>
                  </div>
                  {myWikiStats.rejected_edits > 0 && (
                    <div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ef4444' }}>{myWikiStats.rejected_edits}</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Rejected</div>
                    </div>
                  )}
                </div>
                <TrustProgressBar trustLevel={myWikiStats.trust_level} cleanApprovedEdits={myWikiStats.clean_approved_edits || 0} />
              </div>
            )}

            {/* Discussion stats row */}
            {myDiscStats && myDiscStats.total_messages > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#555' }}>Discussions</span>
                  <DiscussionBadge discussionLevel={myDiscStats.discussion_level} />
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0369a1' }}>{myDiscStats.total_messages}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Posts</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#333' }}>{myDiscStats.total_topics}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Topics</div>
                  </div>
                </div>
                <DiscussionProgressBar discussionLevel={myDiscStats.discussion_level} totalMessages={myDiscStats.total_messages} />
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          background: '#f8f9fa',
          borderRadius: '10px 10px 0 0',
          overflow: 'hidden',
          border: '1px solid #e9ecef',
          borderBottom: 'none'
        }}>
          <button style={tabStyle('wiki')} onClick={() => setActiveTab('wiki')}>
            📝 Wiki Contributors
          </button>
          <button style={tabStyle('discussions')} onClick={() => setActiveTab('discussions')}>
            💬 Discussion Contributors
          </button>
        </div>

        {/* Leaderboard */}
        <div style={{
          background: 'white',
          borderRadius: '0 0 10px 10px',
          border: '1px solid #e9ecef',
          overflow: 'hidden'
        }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              Loading contributors...
            </div>
          ) : leaders.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              {activeTab === 'wiki'
                ? 'No wiki contributions yet. Be the first!'
                : 'No discussion contributions yet. Start a conversation!'}
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: activeTab === 'wiki' ? '50px 1fr auto auto' : '50px 1fr auto auto auto',
                gap: '1rem',
                padding: '0.75rem 1.25rem',
                background: '#fafbfc',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                <div>Rank</div>
                <div>Contributor</div>
                {activeTab === 'wiki' ? (
                  <>
                    <div style={{ textAlign: 'center' }}>Approved</div>
                    <div style={{ textAlign: 'center' }}>Badge</div>
                  </>
                ) : (
                  <>
                    <div style={{ textAlign: 'center' }}>Posts</div>
                    <div style={{ textAlign: 'center' }}>Topics</div>
                    <div style={{ textAlign: 'center' }}>Badge</div>
                  </>
                )}
              </div>

              {/* Table Rows */}
              {leaders.map((entry, i) => {
                const rank = i + 1;
                const isMe = user?.userId === entry.user_id;
                return (
                  <div
                    key={entry.user_id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: activeTab === 'wiki' ? '50px 1fr auto auto' : '50px 1fr auto auto auto',
                      gap: '1rem',
                      padding: '0.75rem 1.25rem',
                      borderBottom: '1px solid #f1f3f5',
                      alignItems: 'center',
                      background: isMe ? '#f5f3ff' : (rank <= 3 ? '#fffef5' : 'white'),
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={e => { if (!isMe && rank > 3) e.currentTarget.style.background = '#fafbfc'; }}
                    onMouseLeave={e => { if (!isMe && rank > 3) e.currentTarget.style.background = 'white'; }}
                  >
                    <div style={{ ...rankStyle(rank), textAlign: 'center' }}>
                      {rankLabel(rank)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
                      <LeaderboardPhoto
                        userId={entry.user_id}
                        displayName={entry.display_name}
                        hasPhoto={entry.has_photo}
                      />
                      <span style={{
                        fontWeight: isMe ? '700' : '500',
                        color: '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {entry.display_name}
                        {isMe && <span style={{ color: '#6c5ce7', fontSize: '0.8rem', marginLeft: '6px' }}>(you)</span>}
                      </span>
                      {entry.is_industry_vip && <IndustryVIPBadge />}
                    </div>
                    {activeTab === 'wiki' ? (
                      <>
                        <div style={{ textAlign: 'center', fontWeight: '600', color: '#6c5ce7', fontSize: '1.05rem' }}>
                          {entry.approved_edits}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <WikiBadge trustLevel={entry.trust_level} />
                          {!TRUST_BADGES[entry.trust_level] && (
                            <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ textAlign: 'center', fontWeight: '600', color: '#0369a1', fontSize: '1.05rem' }}>
                          {entry.total_messages}
                        </div>
                        <div style={{ textAlign: 'center', fontWeight: '600', color: '#555', fontSize: '1.05rem' }}>
                          {entry.total_topics}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <DiscussionBadge discussionLevel={entry.discussion_level} />
                          {!DISCUSSION_BADGES[entry.discussion_level] && (
                            <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Badge Legend — contextual to active tab */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '1.25rem 1.5rem',
          border: '1px solid #e9ecef',
          marginTop: '1.5rem'
        }}>
          {activeTab === 'wiki' ? (
            <>
              <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                How Wiki Trust Levels Work
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {TRUST_THRESHOLDS.map(tier => (
                  <div key={tier.level} style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    fontSize: '0.9rem', color: '#555'
                  }}>
                    <WikiBadge trustLevel={tier.level} />
                    <span>
                      — {tier.required}+ clean approved edits
                      {tier.level === 2 && <em style={{ color: '#4f46e5' }}> (auto-approve enabled for Pro)</em>}
                    </span>
                  </div>
                ))}
              </div>
              <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.85rem', color: '#9ca3af' }}>
                "Clean" approvals are edits approved by moderators without modification.
                Trust levels are available to Pro subscribers.
              </p>
            </>
          ) : (
            <>
              <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                How Discussion Levels Work
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {DISCUSSION_THRESHOLDS.map(tier => (
                  <div key={tier.level} style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    fontSize: '0.9rem', color: '#555'
                  }}>
                    <DiscussionBadge discussionLevel={tier.level} />
                    <span>— {tier.required}+ posts in discussions</span>
                  </div>
                ))}
              </div>
              <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.85rem', color: '#9ca3af' }}>
                Every reply or topic you post counts toward your discussion level.
              </p>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', padding: '1.5rem 0 0.5rem', color: '#aaa', fontSize: '0.8rem' }}>
          AmpNebula is a product of QorVertex LLC · © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default ContributorsScreen;
